;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:localcoin":true});}catch(e){}}();import { createHmac, timingSafeEqual } from 'crypto';

export interface E2EBypassTokenPayload {
  /** Token expiration timestamp (Unix timestamp in ms) */
  exp: number;
}

/**
 * Token time-to-live in milliseconds (default: 10 minutes)
 */
export const TOKEN_TTL_MS = 10 * 60 * 1000;

/**
 * Token TTL in seconds (for cookie Max-Age)
 */
export const TOKEN_TTL_SECONDS = 600;

export const E2E_BYPASS_COOKIE_NAME = 'E2E_BYPASS';

/**
 * Retrieves and validates the bypass secret from environment variables
 *
 * @throws {Error} If E2E_BYPASS_SECRET is not set or too short
 * @returns The validated secret string
 */
const getSecret = (): string => {
  const secret = process.env.E2E_BYPASS_SECRET;

  if (!secret) {
    throw new Error('E2E_BYPASS_SECRET environment variable is not set');
  }

  return secret;
};

/**
 * Validates the structure of a parsed payload
 *
 * @param payload - The parsed payload object to validate
 * @returns True if the payload has the required structure
 */
const isValidPayload = (payload: unknown): payload is E2EBypassTokenPayload => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const p = payload as Record<string, unknown>;

  return typeof p.exp === 'number';
};

/**
 * Generates a signed HMAC token
 *
 * @returns Signed token in format: base64url(payload).base64url(signature)
 *
 * @example
 * ```typescript
 * const token = generateE2EBypassToken();
 * ```
 */
export const generateE2EBypassToken = (): string => {
  const secret = getSecret();

  const payload: E2EBypassTokenPayload = {
    exp: Date.now() + TOKEN_TTL_MS,
  };

  const payloadString = JSON.stringify(payload);
  const payloadBase64 = Buffer.from(payloadString).toString('base64url');

  const signature = createHmac('sha256', secret).update(payloadBase64).digest('base64url');

  return `${payloadBase64}.${signature}`;
};

/**
 * Verifies and decodes a signed E2E bypass token
 *
 * Performs the following validations:
 * 1. Token format (must have exactly 2 parts: payload.signature)
 * 2. HMAC signature verification (timing-safe comparison)
 * 3. Payload structure validation
 * 4. Expiration time check
 *
 * @param token - The token string to verify
 * @returns Decoded payload if valid, null otherwise
 *
 */
export const verifyE2EBypassToken = (token: string): E2EBypassTokenPayload | null => {
  try {
    const secret = getSecret();
    const parts = token.split('.');

    if (parts.length !== 2) {
      return null;
    }

    const [payloadBase64, signatureBase64] = parts;

    if (!payloadBase64 || !signatureBase64) {
      return null;
    }

    const expectedSignature = createHmac('sha256', secret)
      .update(payloadBase64)
      .digest('base64url');

    const expectedBuffer = Buffer.from(expectedSignature);
    const actualBuffer = Buffer.from(signatureBase64);

    if (expectedBuffer.length !== actualBuffer.length) {
      return null;
    }

    if (!timingSafeEqual(expectedBuffer, actualBuffer)) {
      return null;
    }

    const payloadString = Buffer.from(payloadBase64, 'base64url').toString('utf-8');
    const payload: E2EBypassTokenPayload = JSON.parse(payloadString);

    if (!isValidPayload(payload)) {
      return null;
    }

    if (Date.now() > payload.exp) {
      return null;
    }

    return payload;
  } catch {
    return null;
  }
};
